import React, { FC, useState } from "react";
import styled from "@emotion/styled";
import { Text, Button, Flex } from "@radix-ui/themes";
import { DateTime } from "luxon";
import Lowlight from "react-lowlight";
import { SessionEventSummarySchema } from "@/domains/openapi/types";
import { atlasRoutes } from "@/app/router/routes";
import { useNavigate } from "react-router";
import {
  TERMINAL_FONT_FAMILY,
  getEventConfig,
  getEventParts,
  getDisplayKind,
} from "./shared/constants";

// Type definition for session event parts
interface SessionEventPart {
  kind: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface SessionEventMessageProps {
  event: SessionEventSummarySchema;
  onDelete?: (eventId: string) => void;
  isDeleting?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderParts = (parts: any[]): React.ReactNode[] => {
  const result: React.ReactNode[] = [];

  if (parts.length === 0) {
    return [<NoContentMessage>-</NoContentMessage>];
  }

  parts.forEach((part, index) => {
    if (part.kind === "MARKDOWN_V1") {
      // MarkdownV1SessionPart - render as markdown content
      if ("content" in part) {
        result.push(
          <div key={`markdown-${index}`} style={{ marginBlock: "2px" }}>
            <Lowlight language="markdown" value={part.content} markers={[]} />
          </div>
        );
      }
    } else if (part.kind === "REASONING_V1") {
      // ReasoningV1SessionPart - render summary with distinctive styling
      if ("summary" in part) {
        result.push(
          <div
            key={`reasoning-v1-${index}`}
            style={{
              display: "block",
              fontStyle: "italic",
              color: "var(--gray-11)",
              backgroundColor: "var(--gray-2)",
              padding: "8px",
              borderRadius: "4px",
              marginBlock: "4px",
              borderLeft: "3px solid var(--gray-6)",
            }}
          >
            💭 {part.summary}
          </div>
        );
      }
    } else if (part.kind === "WEB_CLIP_V1") {
      // WebClipV1SessionPart - render web clip info
      if ("captured_website_url" in part) {
        const title = part.captured_website_title ?? part.captured_website_url;
        result.push(
          <div
            key={`web-clip-v1-${index}`}
            style={{
              display: "block",
              padding: "8px",
              backgroundColor: "var(--blue-2)",
              borderRadius: "4px",
              marginBlock: "4px",
              borderLeft: "3px solid var(--blue-6)",
            }}
          >
            <div style={{ fontWeight: "500", marginBottom: "4px" }}>🔗 Web Clip: {title}</div>
            {part.clipped_text_content && (
              <div style={{ fontSize: "0.9em", color: "var(--gray-11)" }}>
                {part.clipped_text_content}
              </div>
            )}
          </div>
        );
      }
    } else if (part.kind === "ARTIFACT_V1") {
      // ArtifactSessionPartV1 - render artifact info
      if ("artifact_kind" in part && "artifact_payload" in part) {
        const payload = part.artifact_payload;
        const isNoteCreated = part.artifact_kind === "NOTE_CREATED_V1";
        const isNoteUpdated = part.artifact_kind === "NOTE_UPDATED_V1";

        if ((isNoteCreated || isNoteUpdated) && "primary_label" in payload) {
          result.push(
            <div
              key={`artifact-v1-${index}`}
              style={{
                display: "block",
                padding: "8px",
                backgroundColor: "var(--green-2)",
                borderRadius: "4px",
                marginBlock: "4px",
                borderLeft: "3px solid var(--green-6)",
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                📝 {isNoteCreated ? "Created" : "Updated"} Note: {payload.primary_label}
                {payload.secondary_label && ` - ${payload.secondary_label}`}
              </div>
              {isNoteUpdated && "prev_primary_label" in payload && (
                <div style={{ fontSize: "0.85em", color: "var(--gray-10)", marginBottom: "4px" }}>
                  Previously: {payload.prev_primary_label}
                  {payload.prev_secondary_label && ` - ${payload.prev_secondary_label}`}
                </div>
              )}
              {"markdown_content" in payload && (
                <div style={{ fontSize: "0.9em", marginTop: "4px" }}>
                  <Lowlight language="markdown" value={payload.markdown_content} markers={[]} />
                </div>
              )}
            </div>
          );
        }
      }
    }
  });

  return result;
};

const formatEventContent = (event: SessionEventSummarySchema) => {
  const parts = getEventParts(event);

  if (parts && Array.isArray(parts)) {
    // Type cast to SessionEventPart[] since we know the structure from getEventParts
    const renderedParts = renderParts(parts as SessionEventPart[]);
    return <>{renderedParts}</>;
  }

  return <Text size="2">No content available</Text>;
};

export const SessionEventMessage: FC<SessionEventMessageProps> = ({
  event,
  onDelete,
  isDeleting = false,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const config = getEventConfig(event.data.kind);
  const { alignment, icon } = config;

  console.log("event", DateTime.fromISO(event.locally_created_at));

  return (
    <MessageContainer alignment={alignment}>
      <MessageBubble alignment={alignment}>
        <MessageHeader>
          <MessageMeta>
            <MessageTime>
              {DateTime.fromISO(event.locally_created_at).toFormat("HH:mm:ss")}
            </MessageTime>
            <MessageIcon>{icon}</MessageIcon>
            <MessageKind>{getDisplayKind(event)}</MessageKind>
          </MessageMeta>
          <MessageActions>
            {onDelete &&
              (confirmDelete ? (
                <Flex gap="1">
                  <TerminalButton
                    size="1"
                    color="red"
                    disabled={isDeleting}
                    onClick={() => onDelete(event.id)}
                  >
                    [y]
                  </TerminalButton>
                  <TerminalButton size="1" variant="soft" onClick={() => setConfirmDelete(false)}>
                    [n]
                  </TerminalButton>
                </Flex>
              ) : (
                <DeleteButton size="1" variant="ghost" onClick={() => setConfirmDelete(true)}>
                  [x]
                </DeleteButton>
              ))}
            <TerminalButton size="1" variant="ghost" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? "[-]" : "[+]"}
            </TerminalButton>
          </MessageActions>
        </MessageHeader>

        <MessageContent>
          {formatEventContent(event)}
          {(() => {
            const artifactId = (event.data as unknown as { value?: { artifact_id?: string } })
              ?.value?.artifact_id;
            return event.data?.kind === "ARTIFACT_CREATED" && artifactId ? (
              <TerminalButton
                size="1"
                variant="ghost"
                onClick={() =>
                  navigate(
                    atlasRoutes.sessionArtifactsView({
                      params: { artifactId },
                    }).path
                  )
                }
              >
                [view artifact]
              </TerminalButton>
            ) : null;
          })()}
          {(() => {
            const operationId = (event.data as unknown as { value?: { operation_id?: string } })
              ?.value?.operation_id;
            return event.data?.kind === "OPERATION_PERFORMED" && operationId ? (
              <TerminalButton
                size="1"
                variant="ghost"
                onClick={() =>
                  navigate(
                    atlasRoutes.sessionOperationsView({
                      params: { operationId },
                    }).path
                  )
                }
              >
                [view operation]
              </TerminalButton>
            ) : null;
          })()}
        </MessageContent>

        {isExpanded && (
          <ExpandedContent>
            <ExpandedTitle>Full Event Data:</ExpandedTitle>
            <ExpandedData>
              <Lowlight language="json" value={JSON.stringify(event.data, null, 2)} markers={[]} />
            </ExpandedData>
          </ExpandedContent>
        )}
      </MessageBubble>
    </MessageContainer>
  );
};

const MessageContainer = styled.div<{ alignment: "left" | "right" }>`
  display: flex;
  margin-bottom: 12px;
  width: 100%;
  justify-content: ${({ alignment }) => (alignment === "right" ? "flex-end" : "flex-start")};
`;

const MessageBubble = styled.div<{ alignment: "left" | "right" }>`
  max-width: 100%;
  padding: 8px 0;
  font-size: 13px;
  font-family: ${TERMINAL_FONT_FAMILY};
  border-left: 2px solid transparent;
  border-left-color: ${({ alignment }) =>
    alignment === "right" ? "var(--green-8)" : "var(--gray-8)"};
  padding-left: 12px;
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MessageIcon = styled.span`
  font-size: 13px;
`;

const MessageKind = styled.span`
  font-size: 13px;
  color: var(--gray-9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessageTime = styled.span`
  font-size: 11px;
  color: var(--gray-10);
`;

const MessageActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 8px;
`;

const MessageContent = styled.div`
  line-height: 1.4;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
`;

const ExpandedContent = styled.div`
  margin-top: 8px;
  padding-top: 4px;
  border-top: 1px dotted var(--gray-7);
`;

const ExpandedTitle = styled.div`
  font-size: 13px;
  color: var(--gray-11);
  margin-bottom: 6px;
  font-weight: 500;
`;

const ExpandedData = styled.div`
  font-size: 13px;
  max-height: 200px;
  overflow: auto;
  padding-left: 16px;
  border-left: 1px solid var(--gray-6);

  & pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;
    font-family: ${TERMINAL_FONT_FAMILY};
    font-size: 13px;
    background-color: transparent;

    & code {
      background-color: transparent;
    }
  }
`;

const TerminalButton = styled(Button)`
  font-size: 11px;
  padding: 1px 3px;
  font-family: ${TERMINAL_FONT_FAMILY};
  min-height: auto;
  height: auto;
  color: var(--gray-10) !important;

  &:hover {
    color: var(--gray-11) !important;
  }
`;

const DeleteButton = styled(Button)`
  font-size: 11px;
  padding: 1px 3px;
  font-family: ${TERMINAL_FONT_FAMILY};
  min-height: auto;
  height: auto;
  color: var(--red-8) !important;

  &:hover {
    color: var(--red-10) !important;
  }
`;

const NoContentMessage = styled.span`
  color: var(--gray-9);
  opacity: 0.5;
`;
